import { useQuery } from '@tanstack/react-query';
import httpClient from '../services/http-client';
import { useAuth } from './use-auth';
import { UserListResponse, Division } from '../types';

/**
 * Hook to fetch the current user's divisions from the API
 * This replaces reading division IDs from JWT token claims
 * Supports internal division management
 */
export function useUserDivisions() {
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['user', 'divisions'],
    queryFn: async () => {
      // Call GET /api/users/me - returns all user records for current email
      const response = await httpClient.get<UserListResponse>('/api/v1/users/me');
      
      // Extract unique divisions from all user records
      const divisionsMap = new Map<string, Division>();
      
      response.users.forEach(user => {
        // Each user has divisionRoles array with division info
        user.divisionRoles.forEach(role => {
          if (role.divisionId && !divisionsMap.has(role.divisionId)) {
            divisionsMap.set(role.divisionId, {
              id: role.divisionId,
              name: role.divisionName || `Division ${role.divisionId.substring(0, 8)}`,
              code: role.divisionCode || undefined,
              active: user.active,
              createdAt: user.createdAt,
              updatedAt: user.updatedAt,
              description: role.divisionName || 'User division'
            });
          }
        });
      });
      
      const divisions = Array.from(divisionsMap.values());
      
      // Also return the user roles for each division
      const divisionRoles = response.users.flatMap(user => 
        user.divisionRoles.map(role => ({
          divisionId: role.divisionId,
          divisionName: role.divisionName,
          role: role.role,
          userId: user.id
        }))
      );
      
      return {
        divisions,
        divisionRoles,
        users: response.users
      };
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch divisions for a specific division ID
 * Useful when you know the division ID and want to verify access
 */
export function useUserDivision(divisionId?: string) {
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['user', 'division', divisionId],
    queryFn: async () => {
      if (!divisionId) return null;
      
      const response = await httpClient.get<UserListResponse>(
        `/api/v1/users/me?divisionId=${divisionId}`
      );
      
      return response.users.length > 0 ? response.users[0] : null;
    },
    enabled: isAuthenticated && !!divisionId,
    staleTime: 5 * 60 * 1000,
  });
}
